import instance from "@store/middleware/api";

/**
 * Client for POST/DELETE /api/claims/submissions. See
 * docu/whitelisting.md §19.12 sub-step 5a.
 */

export type SubmitUrlResponse =
  | { kind: "matched"; claimId: string; jobId: string }
  | { kind: "pending"; submissionId: string; videoId: string; expiresAt: string; videoLooksDeleted?: boolean };

export type MyClaimVideoStatus =
  | "Whitelisted"
  | "Ready to Release"
  | "Analyzing"
  | "Searching"
  | "No Claim Detected"
  | "Private Video"
  | "Credit Text"
  | "Credit Missing"
  | "Music Use"
  | "Not Covered"
  | "Claim Limit Reached";

export interface MyClaimDto {
  id: string;
  /** Present when the row is backed by a pending_url_submissions record.
      Drives the Submit again / Remove button wiring on `No Claim Detected`
      cards. */
  submissionId?: string;
  videoTitle: string;
  videoUrl: string;
  trackTitle: string;
  trackArtist: string;
  status: MyClaimVideoStatus;
  /** ISO string of the runner's last verdict, null if it has never run. The
      claims page uses it to tell that a RE-check has finished — `status` cannot,
      because a re-run reaching the same verdict looks identical to no run. */
  checkedAt?: string | null;
  /** The runner has judged this claim more than once — the card says "we looked
      again and it is still wrong" instead of the first-time instructions. */
  reChecked?: boolean;
  /** Matched track's licence tier (`standard` / `plus`) — a single YouTube
      licence is priced by it. */
  licenseTier?: string;
  /** The matched catalogue track's name — may differ from `trackTitle`, which is
      the provider's asset title. The cart shows this, since it is what is being
      paid for. */
  matchedTrackName?: string;
  date: string;
  channelName?: string;
  /** YouTube channel URL built from `channelId`, used to link the channel
      name on each card. Undefined when the claim has no channelId (legacy). */
  channelUrl?: string;
  /** Raw YouTube channel id — used to filter claims by channel and match
      them to the per-channel credit rows. */
  channelId?: string;
  requiredTier?: "Premium";
  /** True when the user hid this card — lives in the Hidden tab. */
  isHidden?: boolean;
  /** True when the user queued this Claim Limit Reached card for next
      cycle — lives in the Queued tab. */
  isQueuedForNextCycle?: boolean;
  /** True when this is an action-required matched claim the user has never
      had on screen — drives the green "New" badge + per-channel new counts. */
  isNew?: boolean;
  /** True when the video has been deleted from YouTube. */
  isVideoDeleted?: boolean;
  /** Set once a waiting card has waited past the API's threshold. */
  takingLonger?: boolean;
  /** True when the free-tier user has at least one unconnected active
      license matching this card's track. The License Now button becomes
      "License Now (uses 1 License)", calling POST /claims/my/:id/use-license. */
  useLicenseAvailable?: boolean;
  /** Global-monotonic `#N` id of the latest `claim_status_events` row for
      this card. Rendered on the card footer in place of the old `#ERR-<uuid>`
      tag. Undefined until the first event has landed (e.g. very fresh
      Analyzing cards before the runner has touched them). */
  eventSequentialId?: number;
  /** The YouTube Licence number for a resolved claim — shown beside the
      License Text button. Undefined until the certificate exists. */
  licenceCode?: string;
  /** Set when this claim's certificate has been voided — the reason is shown. */
  licenceVoidReason?: string;
  /** ISO date the Content ID claim was released (removed from the video).
      Present only for Whitelisted claims — shown as "Claim removed on <date>". */
  releasedAt?: string;
  /** The licence is active but the removal request has not gone out yet — the
      day's provider allowance is spent. The card says the removal follows
      within 24 hours rather than claiming it is already done. */
  releasePending?: boolean;
  /** The track's real Credit Text, composed backend-side by the same helper the
      claims runner and the licence certificate use. */
  creditText?: string;
  /** A released video whose Credit Text a post-release re-check found missing
      (ToU §17.3). Status is projected as "Credit Missing" — action-required. */
  creditMissing?: boolean;
  /** Held from releasing because the account's whitelisting is suspended
      (§17.4) — shown with a "Paused" badge until the suspension lifts. */
  paused?: boolean;
  /** The account's whitelisting is suspended — every card suppresses its
      action buttons (only the Credit-Missing "Fix issue" stays). */
  accountSuspended?: boolean;
  /** ISO time of the last post-release credit re-check. */
  lastCreditCheckAt?: string;
  /** Live `atLimit` for this card's channel as of the read. When true,
      the frontend overrides Ready-to-Release rendering to Claim Limit
      Reached, even though the runner hasn't (re-)stamped the verdict yet.
      Set only for subscription-user rows with a connected channel. */
  channelAtLimit?: boolean;
  /** Resolved track id from the catalog (matched by `assetTitle`). Powers
      the Not Covered card's Buy Single License button which dispatches
      addToCart with the real trackId. */
  trackId?: string;
  /** Track slug — used for deep-linking and license-modal URL params. */
  trackSlug?: string;
  /** Track cover image URL — rendered as the cart-row thumbnail. */
  coverUrl?: string;
}

export interface MyClaimsCounts {
  actionRequired: number;
  underReview: number;
  resolved: number;
}

export async function getMyClaims(): Promise<MyClaimDto[]> {
  const { data } = await instance.get<MyClaimDto[]>("/claims/my");
  return data;
}

export async function getMyClaimsCounts(): Promise<MyClaimsCounts> {
  const { data } = await instance.get<MyClaimsCounts>("/claims/my/counts");
  return data;
}

export interface AutoReleaseState {
  autoReleaseClaims: boolean;
  /** Set true by a plan-change hook. While true, the /account/claims page
      shows a mandatory modal asking the user to re-pick auto-release.
      Cleared by any PUT to /claims/auto-release. */
  autoReleaseChoiceRequired: boolean;
  readyCount: number;
}

export async function getAutoRelease(): Promise<AutoReleaseState> {
  const { data } = await instance.get<AutoReleaseState>("/claims/auto-release");
  return data;
}

/** `releasedClaimIds` are the claims the server ACTUALLY released on an OFF→ON
    flip — the claims page animates exactly those rather than guessing from its
    own list, so a sweep the user cancelled halfway animates only what really
    happened and leaves the untouched cards in place. */
export async function setAutoRelease(enabled: boolean, opts?: { drain?: boolean }): Promise<{ autoReleaseClaims: boolean; releasedCount: number; releasedClaimIds?: string[] }> {
  const { data } = await instance.put("/claims/auto-release", { enabled, ...(opts?.drain === false ? { drain: false } : {}) });
  return data;
}

/** Manual release — "License Now" button. Releases a single Ready to Release claim.
    `channelHitLimit` is true when THIS release was the one that took the
    channel over its monthly credit cap — the UI uses it to flip the
    per-channel banner from "near limit" to "limit reached" without
    waiting for the credits poll. */
/** `auto` marks the call as one step of an auto-resolve run rather than a click
    on a single card. It decides how the release is recorded: the claim timeline
    logs it as done by the system, and the claim is added to the auto-resolve
    toggle's audit row (ToU §9.5). Without it three automatic releases would read
    as three deliberate clicks. */
export async function releaseClaim(claimId: string, opts?: { auto?: boolean }): Promise<{ ok: boolean; releasedAt: string; channelHitLimit?: boolean }> {
  const { data } = await instance.post(`/claims/my/${claimId}/release`, opts?.auto ? { auto: true } : {});
  return data;
}

/** Use License & Resolve — free-tier flow when an unconnected license matches the claim's
    track. Backend attaches the oldest unconnected license to this video URL, then releases
    the claim on Songmate. */
export async function useLicenseOnClaim(claimId: string): Promise<{ ok: boolean; releasedAt: string; licenseId: string }> {
  const { data } = await instance.post(`/claims/my/${claimId}/use-license`);
  return data;
}

/** Re-run — "Submit Again" on a matched claim. Re-enqueues the runner. */
export async function reRunClaim(claimId: string): Promise<{ ok: boolean; jobId: string }> {
  const { data } = await instance.post(`/claims/my/${claimId}/re-run`);
  return data;
}

export interface ClaimSubmissionError {
  code: string;
  message: string;
}

export async function submitClaimUrl(url: string): Promise<SubmitUrlResponse> {
  const { data } = await instance.post<SubmitUrlResponse>("/claims/submissions", { url });
  return data;
}

export interface ResolvedVideoDto {
  videoId: string;
  exists: boolean;
  /** Present when exists + oEmbed returned a body (public videos). Null for
   *  unlisted (oEmbed 401 has no body) — show a thumbnail-only preview then. */
  title?: string | null;
  channelName?: string | null;
  thumbnailUrl?: string;
}

/** Resolve a YouTube video URL to its title + channel + thumbnail WITHOUT
 *  submitting it — mirrors `resolveUserChannel`. Server-side oEmbed (the browser
 *  can't call YouTube oEmbed directly — CORS). Powers the submit preview card. */
export async function resolveClaimVideo(url: string): Promise<ResolvedVideoDto> {
  const { data } = await instance.post<ResolvedVideoDto>("/claims/submissions/resolve", { url });
  return data;
}

/** Reset the wait window on an existing pending submission. Used by the
    "Submit again" button on `No Claim Detected` cards. */
export async function resubmitPending(submissionId: string): Promise<{ submissionId: string; expiresAt: string }> {
  const { data } = await instance.post(`/claims/submissions/${submissionId}/resubmit`);
  return data;
}

/** Hard-delete a pending submission. Used by the "Remove" button. */
export async function removePendingSubmission(submissionId: string): Promise<void> {
  await instance.delete(`/claims/submissions/${submissionId}`);
}

/** Hide a pending submission — mirror of hideClaim for pending rows. */
export async function hidePendingSubmission(submissionId: string): Promise<void> {
  await instance.post(`/claims/submissions/${submissionId}/hide`);
}

/** Unhide a pending submission — mirror of unhideClaim for pending rows. */
export async function unhidePendingSubmission(submissionId: string): Promise<void> {
  await instance.post(`/claims/submissions/${submissionId}/unhide`);
}

/* ── my-claims card actions (§20.6) ────────────────────────────────── */

/** Hide a claim — moves it into the Hidden tab. */
export async function hideClaim(claimId: string): Promise<void> {
  await instance.post(`/claims/my/${claimId}/hide`);
}

/** Mark claims "viewed" (claimViewedAt = now()) as their action-required cards
    scroll into view — clears their green "New" badge on the user's NEXT page
    load. Fire-and-forget; batched + debounced by the caller. */
export async function markClaimsViewed(claimIds: string[]): Promise<void> {
  if (!claimIds.length) return;
  await instance.post(`/claims/my/viewed`, { claimIds });
}

/** User confirms the video was deleted — marks it as video_deleted. */
export async function confirmVideoDeleted(claimId: string): Promise<void> {
  await instance.post(`/claims/my/${claimId}/confirm-deleted`);
}

/** Dismiss a deleted-video card — hides it without checking video existence. */
export async function dismissClaim(claimId: string): Promise<void> {
  await instance.post(`/claims/my/${claimId}/dismiss`);
}

/** Unhide a claim — returns it to its natural tab. */
export async function unhideClaim(claimId: string): Promise<void> {
  await instance.post(`/claims/my/${claimId}/unhide`);
}

/** Queue a Claim Limit Reached card for next billing cycle. Runner
    re-checks at cycle reset. */
export async function queueClaimForNextCycle(claimId: string): Promise<void> {
  await instance.post(`/claims/my/${claimId}/queue-next-cycle`);
}

/** Unqueue — remove from the Queued tab. */
export async function unqueueClaim(claimId: string): Promise<void> {
  await instance.post(`/claims/my/${claimId}/unqueue`);
}

/* ── subscription downgrade channel selection ─────────────────────── */

/** Persist the user's channel-keep selection from the downgrade picker.
    Called from DowngradeChannelPickerModal.onConfirm. */
export async function setDowngradeKeepChannels(
  subscriptionId: string,
  keepChannelIds: string[],
): Promise<{ ok: boolean; keepChannelIds: string[] }> {
  const { data } = await instance.post(
    `/subscriptions/${subscriptionId}/downgrade-channels`,
    { keepChannelIds },
  );
  return data;
}

/** Cancel the pending downgrade entirely. Clears pendingChannels /
    pendingClaimsPerChannel / pendingDowngradeKeepChannelIds on the
    subscription row. Mirrors the existing "Keep Current Plan" banner
    button in AccountSubscription. */
export async function cancelPendingDowngrade(): Promise<void> {
  await instance.post("/stripe/subscriptions/cancel-downgrade");
}

/** Fetch the authenticated user's active subscription (null if free). */
export async function getMySubscription(): Promise<any | null> {
  const { data } = await instance.get("/subscriptions/my");
  return data;
}

/** Fetch the authenticated user's connected channels. */
export async function getMyChannels(): Promise<{ channels: any[]; cap: number }> {
  const { data } = await instance.get("/user-channels");
  return data;
}

/** Extract the structured `{ code, message }` payload our backend returns
    for HttpException errors. Falls back to a generic shape. */
export function parseClaimSubmissionError(err: any): ClaimSubmissionError {
  // The error interceptor rejects with `error.response.data` itself, so the
  // payload usually IS the error object. Accept either shape — reading only
  // err.response.data silently produced "unknown_error" for every refusal the
  // backend took the trouble to name.
  const data = err?.response?.data ?? err;
  if (data && typeof data === "object" && data.code) {
    return { code: String(data.code), message: String(data.message || "") };
  }
  return {
    code: "unknown_error",
    message: err?.message || "Something went wrong. Please try again.",
  };
}
